import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { JobPostLocationType } from '~/api/jobPosts/types';

import { useJobPosts } from '~/api/jobPosts/useJobPosts';
import { ErrorState, Pagination } from '~/components';
import { Field, Select } from '~/components/Form';

import { JobPostCard } from './components/JobPostCard';
import { JobPostsList } from './components/JobPostsList';
import { useActivityOptions, useLocationTypeOptions, useRegionOptions } from './hooks';
import { JobPostFilterChips } from './JobPostFilterChips';
import { filtersStyle, filtersWrapperStyle, itemStyle, listJobsStyle, resultCountStyle } from './JobPosts.css';
import { useJobPostFilters, useJobPostPagination } from './JobPosts.nuqs';

export const JobPosts = () => {
  const { t } = useTranslation(['common', 'jobPosts', 'community']);

  const activityOptions = useActivityOptions();
  const regionOptions = useRegionOptions();
  const locationTypeOptions = useLocationTypeOptions();

  // Filters
  const [paginationQuery, setPaginationQuery] = useJobPostPagination();
  const listRef = useRef<HTMLDivElement>(null);
  const filters = useJobPostFilters();

  const handleLocationTypeChange = (locationType: JobPostLocationType | null) => {
    if (locationType === 'Remote') {
      filters.regions.onChange([]);
    }
    filters.locationType.onChange(locationType);
  };

  // Query
  const {
    data: jobPostsResponse,
    isError,
    isPending,
    isPlaceholderData,
  } = useJobPosts({
    activityIds: filters.activityIds.value,
    locationType: filters.locationType.value,
    regions: filters.regions.value,
    pagination: paginationQuery,
  });

  const jobPosts = jobPostsResponse?.data ?? [];
  const totalResults = jobPostsResponse?.pagination.totalResults;

  return (
    <>
      {/* Filters */}
      <div className={filtersWrapperStyle}>
        <div className={filtersStyle}>
          <Field.Root>
            <Field.Label srOnly>{t('jobPosts:overview.filters.activities')}</Field.Label>
            <Select.Root items={activityOptions} multiple onValueChange={filters.activityIds.onChange} value={filters.activityIds.value}>
              <Select.Button placeholder={t('jobPosts:overview.filters.activities')} />
              <Select.List>
                {activityOptions.map((option) => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.List>
            </Select.Root>
          </Field.Root>

          <Field.Root disabled={filters.locationType.value === 'Remote'}>
            <Field.Label srOnly>{t('jobPosts:overview.filters.regions')}</Field.Label>
            <Select.Root items={regionOptions} multiple onValueChange={filters.regions.onChange} value={filters.regions.value}>
              <Select.Button placeholder={t('jobPosts:overview.filters.regions')} />
              <Select.List>
                {regionOptions.map((option) => (
                  <Select.Item key={String(option.value)} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.List>
            </Select.Root>
          </Field.Root>

          <Field.Root>
            <Field.Label srOnly>{t('jobPosts:overview.filters.locationType')}</Field.Label>
            <Select.Root items={locationTypeOptions} onValueChange={handleLocationTypeChange} value={filters.locationType.value}>
              <Select.Button placeholder={t('jobPosts:overview.filters.locationType')} />
              <Select.List>
                {locationTypeOptions.map((option) => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.List>
            </Select.Root>
          </Field.Root>
        </div>

        <JobPostFilterChips activityOptions={activityOptions} locationTypeOptions={locationTypeOptions} regionOptions={regionOptions} />

        {/* Result count */}
        <div className={resultCountStyle}>
          <span>{isPending ? t('jobPosts:overview.loading') : t('jobPosts:overview.resultCount', { count: totalResults ?? 0 })}</span>
        </div>
      </div>

      {/* List jobs */}
      {!isError ? (
        <div className={listJobsStyle} ref={listRef}>
          <JobPostsList isLoading={isPending || isPlaceholderData}>
            {jobPosts.map((jobPost) => (
              <li className={itemStyle} key={jobPost.id}>
                <JobPostCard jobPost={jobPost} />
              </li>
            ))}
          </JobPostsList>

          <Pagination
            onChange={(newPagination) => setPaginationQuery(newPagination)}
            pageIndex={paginationQuery.pageIndex}
            pageSize={paginationQuery.pageSize}
            scrollTargetRef={listRef}
            showPageSizeOptions
            totalResults={totalResults ?? 0}
          />
        </div>
      ) : (
        <ErrorState onRetry={() => window.location.reload()} title={t('jobPosts:overview.error.title')} />
      )}
    </>
  );
};
